import * as React from 'react'
import { CalendarDate, Time } from '@internationalized/date'
import type { DateValue, TimeValue } from 'react-aria-components'
import { CaretLeft, CaretRight, Plus, Trash } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { Button } from './button'
import { SegmentedControl } from './segmented-control'
import { Input } from './input'
import { FieldLabel, FieldHint } from './field'
import { Checkbox } from './checkbox'
import { Radio, RadioGroup } from './radio'
import { ColorDot } from './color-dot'
import { DateField } from './date-field'
import { TimeField } from './time-field'
import { AutoGrowTextarea } from './auto-grow-textarea'
import { OverlayWrapper, Backdrop } from './overlay'
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from './dialog'
import { softSurface, softText, accentBg } from './badge-colors'
import {
  type CalendarEvent,
  type CalendarEventColor,
  type CalendarView,
  type SpanningSegment,
  type TimedLayoutItem,
  type WeekStart,
  addDays,
  addMonths,
  eventsForDay,
  isBarEvent,
  layoutBarEvents,
  layoutDayTimedEvents,
  formatDayTitle,
  formatHour,
  formatMonthYear,
  formatTime,
  formatWeekdayNarrow,
  formatWeekdayShort,
  formatWeekRange,
  getMonthGrid,
  getWeekDays,
  isSameDay,
  isSameMonth,
  MINUTES_PER_DAY,
  minutesSinceMidnight,
  startOfDay,
} from './event-calendar-utils'

/**
 * EventCalendar — a scheduling calendar with month / week / day views, built
 * natively on First Bite tokens and design-system primitives (no third-party
 * calendar library). Events carry a badge-palette accent color so their chips
 * and time-grid blocks match the rest of the DS. A built-in add/edit modal
 * (composed from `Dialog` + `Input` + `RadioGroup` + `Checkbox`) lets users
 * create, recolor, and delete events; clicking an empty day or time slot opens
 * it pre-filled.
 *
 *   <EventCalendar defaultEvents={events} defaultView="month" />
 *
 * Events, `view`, and the focused `date` are each controlled-or-uncontrolled
 * (pass the value + change handler to control, or the `default*` to let the
 * calendar own it). Fully responsive: the toolbar wraps on small screens, the
 * month grid shrinks its chips, and the week time-grid scrolls horizontally on
 * phones while filling the width on desktop. Colors resolve through the shared
 * `badge-colors` maps; `now` is injectable so today-highlighting and the
 * current-time line stay deterministic in tests/snapshots.
 */

const VIEW_OPTIONS: { value: CalendarView; label: string }[] = [
  { value: 'month', label: 'Month' },
  { value: 'week', label: 'Week' },
  { value: 'day', label: 'Day' },
]

/** The selectable accent colors offered in the add/edit modal. */
const EVENT_COLOR_OPTIONS: { value: CalendarEventColor; label: string }[] = [
  { value: 'primary', label: 'Basil' },
  { value: 'blue', label: 'Blue' },
  { value: 'violet', label: 'Violet' },
  { value: 'amber', label: 'Amber' },
  { value: 'error', label: 'Red' },
]

/** Row height for one hour in the week/day time grid. */
const HOUR_HEIGHT = 56
const HOURS = Array.from({ length: 24 }, (_, h) => h)

/** Chip/background classes for an event's accent color. */
const eventChipClass = (color: CalendarEventColor = 'primary'): string =>
  cn(softSurface[color], softText[color])

// ── controllable state helper ────────────────────────────────────────────────

/**
 * Minimal controlled-or-uncontrolled state, matching the pattern used across
 * fb-ui (`RadioGroup`, `OverlayWrapper`). Returns the current value and a setter
 * that updates internal state only when uncontrolled and always fires `onChange`.
 */
function useControllable<T>(
  value: T | undefined,
  defaultValue: T,
  onChange?: (next: T) => void
): [T, (next: T) => void] {
  const isControlled = value !== undefined
  const [internal, setInternal] = React.useState(defaultValue)
  const current = isControlled ? (value as T) : internal
  const set = React.useCallback(
    (next: T) => {
      if (!isControlled) setInternal(next)
      onChange?.(next)
    },
    [isControlled, onChange]
  )
  return [current, set]
}

// ── add/edit modal ───────────────────────────────────────────────────────────

interface EditingState {
  event: CalendarEvent
  isNew: boolean
}

interface EventDialogFormProps {
  editing: EditingState
  onSave: (event: CalendarEvent) => void
  onDelete: (id: string) => void
  onClose: () => void
}

// JS Date ↔ react-aria date/time values (the segmented DateField/TimeField
// speak @internationalized/date, the calendar model speaks native Date).
const toDateValue = (d: Date): CalendarDate =>
  new CalendarDate(d.getFullYear(), d.getMonth() + 1, d.getDate())
const toTimeValue = (d: Date): Time => new Time(d.getHours(), d.getMinutes())
const combine = (date: DateValue, time: TimeValue): Date =>
  new Date(date.year, date.month - 1, date.day, time.hour, time.minute)

/** Small labelled wrapper for the date/time segmented fields. */
const FieldBlock = ({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) => (
  <div className="space-y-1.5">
    <FieldLabel className="block">{label}</FieldLabel>
    {children}
  </div>
)

/**
 * The modal body. Local field state is seeded from `editing.event`; the parent
 * remounts it via `key` so it re-initializes cleanly for each open (no effect).
 * Date + start/end times are editable (segmented `DateField`/`TimeField`), and
 * a `description` textarea captures notes.
 */
const EventDialogForm = ({
  editing,
  onSave,
  onDelete,
  onClose,
}: EventDialogFormProps) => {
  const { event, isNew } = editing
  const [title, setTitle] = React.useState(event.title)
  const [color, setColor] = React.useState<CalendarEventColor>(
    event.color ?? 'primary'
  )
  const [allDay, setAllDay] = React.useState(!!event.allDay)
  const endSource = event.end ?? event.start
  const [startDate, setStartDate] = React.useState<DateValue>(
    toDateValue(event.start)
  )
  const [startTime, setStartTime] = React.useState<TimeValue>(
    toTimeValue(event.start)
  )
  const [endDate, setEndDate] = React.useState<DateValue>(toDateValue(endSource))
  const [endTime, setEndTime] = React.useState<TimeValue>(
    event.end
      ? toTimeValue(event.end)
      : new Time((event.start.getHours() + 1) % 24, event.start.getMinutes())
  )
  const [description, setDescription] = React.useState(event.description ?? '')
  const [titleError, setTitleError] = React.useState(false)
  const [rangeError, setRangeError] = React.useState(false)
  const titleId = React.useId()
  const descId = React.useId()

  const clearRangeError = () => rangeError && setRangeError(false)

  const handleSave = () => {
    if (!title.trim()) {
      setTitleError(true)
      return
    }
    const midnight = new Time(0, 0)
    const start = combine(startDate, allDay ? midnight : startTime)
    const end = combine(endDate, allDay ? midnight : endTime)
    // All-day may end on the same day (single-day); timed must be strictly after.
    const invalid = allDay
      ? end.getTime() < start.getTime()
      : end.getTime() <= start.getTime()
    if (invalid) {
      setRangeError(true)
      return
    }
    onSave({
      ...event,
      title: title.trim(),
      color,
      allDay,
      start,
      end,
      description: description.trim() || undefined,
    })
  }

  return (
    <Dialog aria-label={isNew ? 'Add event' : 'Edit event'} className="max-w-md">
      <DialogHeader>
        <DialogTitle>{isNew ? 'Add event' : 'Edit event'}</DialogTitle>
        <DialogDescription>
          {isNew
            ? 'Set the details for your new event.'
            : 'Update the details for this event.'}
        </DialogDescription>
      </DialogHeader>

      <DialogBody className="space-y-4">
        <div className="space-y-1.5">
          <FieldLabel htmlFor={titleId} className="block">
            Event title
          </FieldLabel>
          <Input
            id={titleId}
            value={title}
            status={titleError ? 'error' : 'default'}
            placeholder="e.g. Kickoff call"
            onChange={(e) => {
              setTitle(e.target.value)
              if (titleError) setTitleError(false)
            }}
          />
          {titleError && <FieldHint status="error">Please add a title.</FieldHint>}
        </div>

        <div className="space-y-3">
          <FieldBlock label="Starts">
            <div className="flex flex-wrap items-center gap-2">
              <DateField
                aria-label="Start date"
                value={startDate}
                onChange={(v) => {
                  if (!v) return
                  setStartDate(v)
                  clearRangeError()
                }}
              />
              {!allDay && (
                <TimeField
                  aria-label="Start time"
                  value={startTime}
                  onChange={(v) => {
                    if (!v) return
                    setStartTime(v)
                    clearRangeError()
                  }}
                />
              )}
            </div>
          </FieldBlock>
          <FieldBlock label="Ends">
            <div className="flex flex-wrap items-center gap-2">
              <DateField
                aria-label="End date"
                value={endDate}
                status={rangeError ? 'error' : 'default'}
                onChange={(v) => {
                  if (!v) return
                  setEndDate(v)
                  clearRangeError()
                }}
              />
              {!allDay && (
                <TimeField
                  aria-label="End time"
                  value={endTime}
                  status={rangeError ? 'error' : 'default'}
                  onChange={(v) => {
                    if (!v) return
                    setEndTime(v)
                    clearRangeError()
                  }}
                />
              )}
            </div>
          </FieldBlock>
          {rangeError && (
            <FieldHint status="error">The end must be after the start.</FieldHint>
          )}
        </div>

        <Checkbox
          checked={allDay}
          onChange={(e) => setAllDay(e.target.checked)}
          label="All day"
        />

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-fbui-gray-700">
            Color
          </legend>
          <RadioGroup
            value={color}
            onValueChange={(v) => setColor(v as CalendarEventColor)}
            className="flex flex-wrap gap-x-4 gap-y-2"
          >
            {EVENT_COLOR_OPTIONS.map((opt) => (
              <Radio
                key={opt.value}
                value={opt.value}
                label={
                  <span className="flex items-center gap-1.5">
                    <ColorDot color={opt.value} />
                    {opt.label}
                  </span>
                }
              />
            ))}
          </RadioGroup>
        </fieldset>

        <div className="space-y-1.5">
          <FieldLabel htmlFor={descId} className="block" optional>
            Description
          </FieldLabel>
          <AutoGrowTextarea
            id={descId}
            value={description}
            rows={2}
            placeholder="Add notes, an agenda, or a location…"
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      </DialogBody>

      <DialogFooter className={cn(!isNew && 'justify-between')}>
        {!isNew && (
          <Button
            variant="danger"
            appearance="outline"
            leftIcon={<Trash />}
            onClick={() => onDelete(event.id)}
          >
            Delete
          </Button>
        )}
        <div className="flex gap-2">
          <Button variant="primary" appearance="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            {isNew ? 'Add event' : 'Save'}
          </Button>
        </div>
      </DialogFooter>
    </Dialog>
  )
}

// ── month view ───────────────────────────────────────────────────────────────

/** How the add modal should be pre-filled when a day/slot is chosen. */
interface AddRequest {
  allDay: boolean
  /** Minutes from midnight for the start (timed events only). */
  startMin?: number
  /** Minutes from midnight for the end (timed events only). */
  endMin?: number
}

/** Spacing scale — `compact` fits the calendar into a modal, card, or panel. */
export type EventCalendarDensity = 'comfortable' | 'compact'

interface ViewProps {
  date: Date
  events: CalendarEvent[]
  weekStartsOn: WeekStart
  now: Date
  density: EventCalendarDensity
  onAdd: (day: Date, req: AddRequest) => void
  onEditEvent: (event: CalendarEvent) => void
  onShowDay: (day: Date) => void
}

/** Small event chip used inside a month cell. */
const MonthEventChip = ({
  event,
  onClick,
}: {
  event: CalendarEvent
  onClick: () => void
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'flex w-full items-center gap-1 rounded px-1.5 py-0.5 text-left text-xs font-medium outline-none transition-colors',
      'focus-visible:ring-2 focus-visible:ring-fbui-ring-brand',
      eventChipClass(event.color)
    )}
  >
    <ColorDot color={event.color ?? 'primary'} size="sm" />
    {!event.allDay && (
      <span className="shrink-0 tabular-nums opacity-80">
        {formatTime(event.start)}
      </span>
    )}
    <span className="truncate">{event.title}</span>
  </button>
)

const MAX_MONTH_CHIPS = 3
/** Height (px) of one spanning bar + the gap below it. */
const BAR_H = 20
const BAR_GAP = 2
/** Vertical offset (px) from a month cell's top to its bar band. */
const MONTH_BAND_TOP = 32

/**
 * A single horizontal bar for an all-day / multi-day event, placed by grid
 * column span + lane. Squared edges (and no side margin) where the event
 * continues past this row so consecutive weeks read as one continuous bar.
 */
const SpanBar = ({
  segment,
  onClick,
}: {
  segment: SpanningSegment
  onClick: () => void
}) => {
  const { event, startIndex, endIndex, lane, continuesBefore, continuesAfter } =
    segment
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        gridColumnStart: startIndex + 1,
        gridColumnEnd: endIndex + 2,
        gridRowStart: lane + 1,
      }}
      className={cn(
        'pointer-events-auto z-10 mx-0.5 flex h-5 items-center gap-1 overflow-hidden rounded px-1.5 text-left text-xs font-medium outline-none focus-visible:ring-2 focus-visible:ring-fbui-ring-brand',
        continuesBefore && 'ml-0 rounded-l-none',
        continuesAfter && 'mr-0 rounded-r-none',
        eventChipClass(event.color)
      )}
    >
      {!continuesBefore && (
        <ColorDot color={event.color ?? 'primary'} size="sm" />
      )}
      <span className="truncate">{event.title}</span>
    </button>
  )
}

/** The lane-packed grid of spanning bars for one row of `columns` days. */
const BarBand = ({
  segments,
  laneCount,
  columns,
  onEdit,
  className,
}: {
  segments: SpanningSegment[]
  laneCount: number
  columns: number
  onEdit: (event: CalendarEvent) => void
  className?: string
}) => (
  <div
    className={cn('grid gap-x-0.5 gap-y-0.5', className)}
    style={{
      gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
      gridTemplateRows: `repeat(${laneCount}, ${BAR_H}px)`,
    }}
  >
    {segments.map((seg) => (
      <SpanBar
        key={seg.event.id}
        segment={seg}
        onClick={() => onEdit(seg.event)}
      />
    ))}
  </div>
)

/** Split a flat 42-day month grid into six week rows. */
const chunkWeeks = (grid: Date[]): Date[][] =>
  Array.from({ length: grid.length / 7 }, (_, i) => grid.slice(i * 7, i * 7 + 7))

/**
 * The month grid: weekday header + week rows. All-day / multi-day events render
 * as continuous lane-packed bars spanning their columns (a shared band per week
 * row); single-day timed events render as dot chips beneath the band.
 */
const MonthView = ({
  date,
  events,
  weekStartsOn,
  now,
  density,
  onAdd,
  onEditEvent,
  onShowDay,
}: ViewProps) => {
  const weeks = chunkWeeks(getMonthGrid(date, weekStartsOn))
  const cellMinH =
    density === 'compact' ? 'min-h-[4.5rem]' : 'min-h-[6rem] sm:min-h-[7rem]'

  return (
    <div className="flex flex-col">
      {/* Weekday header */}
      <div className="grid grid-cols-7 border-b border-fbui-gray-200">
        {weeks[0].map((d) => (
          <div
            key={d.getDay()}
            className="px-2 py-2 text-center text-xs font-semibold uppercase tracking-wide text-fbui-gray-500"
          >
            <span className="hidden sm:inline">{formatWeekdayShort(d)}</span>
            <span className="sm:hidden">{formatWeekdayNarrow(d)}</span>
          </div>
        ))}
      </div>

      {/* Week rows */}
      {weeks.map((weekDays) => {
        const { segments, laneCount } = layoutBarEvents(events, weekDays)
        const bandHeight = laneCount * (BAR_H + BAR_GAP)
        return (
          <div key={weekDays[0].toISOString()} className="relative">
            <div className="grid grid-cols-7">
              {weekDays.map((day) => {
                // Bars are drawn in the overlay; cells host only single-day timed events.
                const single = eventsForDay(events, day).filter(
                  (e) => !isBarEvent(e)
                )
                const visible = single.slice(0, MAX_MONTH_CHIPS)
                const overflow = single.length - visible.length
                const outside = !isSameMonth(day, date)
                const today = isSameDay(day, now)
                return (
                  <div
                    key={day.toISOString()}
                    className={cn(
                      'relative border-b border-r border-fbui-gray-100',
                      cellMinH,
                      '[&:nth-child(7n)]:border-r-0',
                      outside && 'bg-fbui-gray-50/60'
                    )}
                  >
                    {/* Full-cell add affordance (behind the content). */}
                    <button
                      type="button"
                      aria-label={`Add event on ${formatDayTitle(day)}`}
                      onClick={() => onAdd(day, { allDay: true })}
                      className="absolute inset-0 outline-none transition-colors hover:bg-fbui-gray-50 focus-visible:bg-fbui-gray-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-fbui-ring-brand"
                    />
                    <div className="pointer-events-none relative flex h-full flex-col gap-1 p-1">
                      <div className="flex justify-end px-0.5">
                        <span
                          className={cn(
                            'flex size-6 items-center justify-center rounded-full text-xs font-medium',
                            outside ? 'text-fbui-gray-300' : 'text-fbui-gray-700',
                            today &&
                              'bg-fbui-basil-600 font-semibold text-fbui-base-white'
                          )}
                        >
                          {day.getDate()}
                        </span>
                      </div>
                      {/* Reserve vertical space for the shared bar band. */}
                      {bandHeight > 0 && (
                        <div aria-hidden="true" style={{ height: bandHeight }} />
                      )}
                      <div className="pointer-events-auto flex flex-col gap-0.5">
                        {visible.map((event) => (
                          <MonthEventChip
                            key={event.id}
                            event={event}
                            onClick={() => onEditEvent(event)}
                          />
                        ))}
                        {overflow > 0 && (
                          <button
                            type="button"
                            onClick={() => onShowDay(day)}
                            className="rounded px-1.5 py-0.5 text-left text-xs font-medium text-fbui-gray-500 outline-none hover:text-fbui-gray-700 focus-visible:ring-2 focus-visible:ring-fbui-ring-brand"
                          >
                            +{overflow} more
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Spanning-bar overlay, aligned to the day columns. */}
            {laneCount > 0 && (
              <div
                className="pointer-events-none absolute inset-x-0 px-1"
                style={{ top: MONTH_BAND_TOP }}
              >
                <BarBand
                  columns={7}
                  segments={segments}
                  laneCount={laneCount}
                  onEdit={onEditEvent}
                />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── week / day time grid ─────────────────────────────────────────────────────

/** Snap drag/tap times to 15-minute increments. */
const SNAP_MIN = 15
/** Blocks shorter than this (px) render title-only so text isn't clipped. */
const COMPACT_HEIGHT = 44

/** Minutes from midnight → CSS pixel offset within a day column. */
const minToPx = (min: number): number => (min / 60) * HOUR_HEIGHT

/**
 * A positioned timed-event block inside a day column. Placed by time (top/
 * height) and, when it overlaps others, by column (left/width) so concurrent
 * events sit side-by-side instead of stacking on top of each other.
 */
const TimeGridEvent = ({
  item,
  onClick,
}: {
  item: TimedLayoutItem
  onClick: () => void
}) => {
  const { event, startMin, endMin, lane, columns } = item
  const top = minToPx(startMin)
  const height = Math.max(minToPx(endMin - startMin), 20)
  const compact = height < COMPACT_HEIGHT
  const widthPct = 100 / columns
  const leftPct = widthPct * lane
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        top,
        height,
        // 2px gutter on each side (matches the old inset-x-1 feel, and separates
        // adjacent columns of overlapping events).
        left: `calc(${leftPct}% + 2px)`,
        width: `calc(${widthPct}% - 4px)`,
      }}
      className={cn(
        'pointer-events-auto absolute z-10 flex flex-col overflow-hidden rounded-md pl-2 pr-1 text-left leading-tight outline-none transition-colors',
        'focus-visible:ring-2 focus-visible:ring-fbui-ring-brand',
        compact ? 'justify-center py-0' : 'py-1',
        eventChipClass(event.color)
      )}
    >
      <span
        className={cn(
          'absolute inset-y-0 left-0 w-1 rounded-l-md',
          accentBg[event.color ?? 'primary']
        )}
      />
      <span className="truncate text-xs font-semibold">{event.title}</span>
      {/* The time line only fits once the block is tall enough. */}
      {!compact && (
        <span className="truncate text-xs opacity-80">
          {formatTime(event.start)}
          {event.end && ` – ${formatTime(event.end)}`}
        </span>
      )}
    </button>
  )
}

/**
 * One day's column. Renders the hour grid (also the keyboard/screen-reader
 * add targets), a mouse drag-to-create overlay, timed-event blocks, and the
 * current-time line.
 *
 * Interaction split: the per-hour `<button>`s handle keyboard + click-to-add
 * (one focusable target per hour). The mouse overlay sits above them, so a
 * mouse press-drag paints a time range and creates an event spanning it; a
 * plain click adds a 1-hour event at that slot. Touch keeps native vertical
 * scroll (`touch-pan-y`) — drag-create is mouse-only. Event blocks render above
 * the overlay so they stay clickable for editing.
 */
const DayColumn = ({
  day,
  events,
  now,
  showCurrentTime,
  onAdd,
  onEditEvent,
}: {
  day: Date
  events: CalendarEvent[]
  now: Date
  showCurrentTime: boolean
  onAdd: (day: Date, req: AddRequest) => void
  onEditEvent: (event: CalendarEvent) => void
}) => {
  // Bar events (all-day / multi-day) render in the all-day row, not the grid.
  // Timed events are laid into side-by-side columns so overlaps don't stack.
  const timedLayout = layoutDayTimedEvents(
    events.filter((e) => !isBarEvent(e)),
    day
  )
  const colRef = React.useRef<HTMLDivElement>(null)
  const dragStartRef = React.useRef<number | null>(null)
  const didDragRef = React.useRef(false)
  const isMouseRef = React.useRef(false)
  const [selection, setSelection] = React.useState<{ a: number; b: number } | null>(
    null
  )

  const nowLine =
    showCurrentTime && isSameDay(day, now) ? minToPx(minutesSinceMidnight(now)) : null

  /** Snapped minute-of-day for a pointer's vertical position. */
  const minuteAt = (clientY: number): number => {
    const rect = colRef.current?.getBoundingClientRect()
    if (!rect) return 0
    const raw = ((clientY - rect.top) / HOUR_HEIGHT) * 60
    const snapped = Math.round(raw / SNAP_MIN) * SNAP_MIN
    return Math.max(0, Math.min(MINUTES_PER_DAY, snapped))
  }

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    isMouseRef.current = e.pointerType === 'mouse'
    dragStartRef.current = minuteAt(e.clientY)
    didDragRef.current = false
    // Mouse begins a drag-select. Touch/pen only records a tap and keeps native
    // scroll (no preventDefault / no capture) — drag-create stays mouse-only.
    if (isMouseRef.current) {
      e.preventDefault()
      setSelection({ a: dragStartRef.current, b: dragStartRef.current })
      e.currentTarget.setPointerCapture(e.pointerId)
    }
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragStartRef.current === null || !isMouseRef.current) return
    const m = minuteAt(e.clientY)
    if (Math.abs(m - dragStartRef.current) >= SNAP_MIN) didDragRef.current = true
    setSelection({ a: dragStartRef.current, b: m })
  }

  const endDrag = (e: React.PointerEvent<HTMLDivElement>, commit: boolean) => {
    if (dragStartRef.current === null) return
    const start = dragStartRef.current
    const wasMouse = isMouseRef.current
    dragStartRef.current = null
    setSelection(null)
    if (!commit) return
    if (wasMouse) {
      const cur = minuteAt(e.clientY)
      const a = Math.min(start, cur)
      const b = Math.max(start, cur)
      // A real drag uses its span (min one snap step); a click adds an hour.
      const endMin =
        didDragRef.current ? (b - a < SNAP_MIN ? a + SNAP_MIN : b) : a + 60
      onAdd(day, { allDay: false, startMin: a, endMin })
    } else {
      // Touch tap → add a 1-hour event at the tapped slot. A scroll fires
      // pointercancel (commit=false) instead, so it won't misfire.
      onAdd(day, { allDay: false, startMin: start, endMin: start + 60 })
    }
  }

  const selectionTop = selection ? minToPx(Math.min(selection.a, selection.b)) : 0
  const selectionHeight = selection
    ? minToPx(Math.abs(selection.b - selection.a))
    : 0

  return (
    <div
      ref={colRef}
      className="relative select-none border-r border-fbui-gray-100 last:border-r-0"
    >
      {/* Hour grid — also the keyboard/screen-reader add targets. */}
      {HOURS.map((hour) => (
        <button
          key={hour}
          type="button"
          aria-label={`Add event on ${formatDayTitle(day)} at ${formatHour(hour)}`}
          onClick={() =>
            onAdd(day, { allDay: false, startMin: hour * 60, endMin: hour * 60 + 60 })
          }
          style={{ height: HOUR_HEIGHT }}
          className="block w-full border-b border-fbui-gray-100 outline-none transition-colors hover:bg-fbui-gray-50 focus-visible:bg-fbui-gray-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-fbui-ring-brand"
        />
      ))}

      {/* Mouse drag/tap overlay (above the buttons, below the events). */}
      <div
        className="absolute inset-0 touch-pan-y"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={(e) => endDrag(e, true)}
        onPointerCancel={(e) => endDrag(e, false)}
      />

      {/* Live drag selection preview. */}
      {selection && (
        <div
          aria-hidden="true"
          style={{ top: selectionTop, height: Math.max(selectionHeight, 2) }}
          className="pointer-events-none absolute inset-x-1 z-10 rounded-md border border-fbui-basil-400 bg-fbui-basil-100/70"
        />
      )}

      {/* Timed events (clickable) + current-time line. */}
      <div className="pointer-events-none absolute inset-0">
        {timedLayout.map((item) => (
          <TimeGridEvent
            key={item.event.id}
            item={item}
            onClick={() => onEditEvent(item.event)}
          />
        ))}
        {nowLine !== null && (
          <div
            className="absolute inset-x-0 z-20 flex items-center"
            style={{ top: nowLine }}
            aria-hidden="true"
          >
            <span className="size-2 shrink-0 rounded-full bg-fbui-red-500" />
            <span className="h-px w-full bg-fbui-red-500" />
          </div>
        )}
      </div>
    </div>
  )
}

interface TimeGridViewProps extends ViewProps {
  variant: 'week' | 'day'
  showCurrentTime: boolean
}

/**
 * The week/day time grid: pinned day headers + optional all-day row above a
 * vertically-scrolling body of hour rows. `variant` sets the column count (7 for
 * week, 1 for day); the week grid keeps a min width so it scrolls horizontally
 * on narrow viewports while filling the width on desktop.
 */
const TimeGridView = ({
  date,
  events,
  weekStartsOn,
  now,
  density,
  variant,
  showCurrentTime,
  onAdd,
  onEditEvent,
}: TimeGridViewProps) => {
  const bodyMaxH = density === 'compact' ? 'max-h-[18rem]' : 'max-h-[34rem]'
  const days = variant === 'week' ? getWeekDays(date, weekStartsOn) : [startOfDay(date)]
  const gridCols =
    variant === 'week'
      ? 'grid-cols-[3.5rem_repeat(7,minmax(0,1fr))]'
      : 'grid-cols-[3.5rem_minmax(0,1fr)]'
  // All-day + multi-day events render as bars in the all-day row.
  const { segments: barSegments, laneCount: barLanes } = layoutBarEvents(
    events,
    days
  )

  return (
    <div className="overflow-x-auto fbui-scroll">
      <div className={cn(variant === 'week' && 'min-w-[44rem]')}>
        {/* Day headers */}
        <div className={cn('grid border-b border-fbui-gray-200', gridCols)}>
          <div className="border-r border-fbui-gray-100" />
          {days.map((day) => {
            const today = isSameDay(day, now)
            return (
              <div
                key={day.toISOString()}
                className="flex flex-col items-center gap-0.5 border-r border-fbui-gray-100 py-2 last:border-r-0"
              >
                <span className="text-xs font-medium uppercase tracking-wide text-fbui-gray-500">
                  {formatWeekdayShort(day)}
                </span>
                <span
                  className={cn(
                    'flex size-7 items-center justify-center rounded-full text-sm font-semibold',
                    today
                      ? 'bg-fbui-basil-600 text-fbui-base-white'
                      : 'text-fbui-gray-800'
                  )}
                >
                  {day.getDate()}
                </span>
              </div>
            )
          })}
        </div>

        {/* All-day / multi-day row */}
        {barLanes > 0 && (
          <div className={cn('grid border-b border-fbui-gray-200', gridCols)}>
            <div className="flex items-start justify-end border-r border-fbui-gray-100 px-2 py-1.5 text-right text-xs font-medium text-fbui-gray-500">
              all-day
            </div>
            <div
              className="p-1"
              style={{ gridColumn: `2 / span ${days.length}` }}
            >
              <BarBand
                columns={days.length}
                segments={barSegments}
                laneCount={barLanes}
                onEdit={onEditEvent}
              />
            </div>
          </div>
        )}

        {/* Scrollable hour body */}
        <div className={cn('overflow-y-auto fbui-scroll', bodyMaxH)}>
          <div className={cn('grid', gridCols)}>
            {/* Hour labels gutter — no horizontal rules here, so the label sits
                clear of the line (the grid lines live in the day columns). The
                label is nudged up half a line so it centers on the hour boundary. */}
            <div className="border-r border-fbui-gray-100">
              {HOURS.map((hour) => (
                <div key={hour} style={{ height: HOUR_HEIGHT }} className="relative">
                  {hour > 0 && (
                    <span className="absolute -top-2 right-2 bg-fbui-base-white pl-1 text-xs text-fbui-gray-500">
                      {formatHour(hour)}
                    </span>
                  )}
                </div>
              ))}
            </div>
            {days.map((day) => (
              <DayColumn
                key={day.toISOString()}
                day={day}
                events={eventsForDay(events, day)}
                now={now}
                showCurrentTime={showCurrentTime}
                onAdd={onAdd}
                onEditEvent={onEditEvent}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── main component ───────────────────────────────────────────────────────────

export interface EventCalendarProps {
  /** Events to render (controlled). */
  events?: CalendarEvent[]
  /** Initial events (uncontrolled). */
  defaultEvents?: CalendarEvent[]
  /** Fires whenever events change via the built-in add/edit/delete modal. */
  onEventsChange?: (events: CalendarEvent[]) => void
  /** Active view (controlled). */
  view?: CalendarView
  /** Initial view (uncontrolled). */
  defaultView?: CalendarView
  onViewChange?: (view: CalendarView) => void
  /** Focused date the view is centered on (controlled). */
  date?: Date
  /** Initial focused date (uncontrolled); defaults to today. */
  defaultDate?: Date
  onDateChange?: (date: Date) => void
  /** First day of the week: 0 = Sunday (default), 1 = Monday. */
  weekStartsOn?: WeekStart
  /**
   * "Now" — used for today-highlighting and the current-time line. Defaults to
   * `new Date()`; pass a fixed value for deterministic tests/snapshots.
   */
  now?: Date
  /** Show the red current-time line in week/day views (default true). */
  showCurrentTime?: boolean
  /**
   * Spacing scale. `compact` shrinks the toolbar, month cells, and time-grid
   * height so the calendar fits inside a modal, card, or panel. Default
   * `comfortable` for a full-page calendar.
   */
  density?: EventCalendarDensity
  className?: string
}

export const EventCalendar = React.forwardRef<HTMLDivElement, EventCalendarProps>(
  (
    {
      events: eventsProp,
      defaultEvents = [],
      onEventsChange,
      view: viewProp,
      defaultView = 'month',
      onViewChange,
      date: dateProp,
      defaultDate,
      onDateChange,
      weekStartsOn = 0,
      now,
      showCurrentTime = true,
      density = 'comfortable',
      className,
    },
    ref
  ) => {
    const compact = density === 'compact'
    const resolvedNow = now ?? new Date()
    const [events, setEvents] = useControllable(
      eventsProp,
      defaultEvents,
      onEventsChange
    )
    const [view, setView] = useControllable(viewProp, defaultView, onViewChange)
    const [date, setDate] = useControllable(
      dateProp,
      defaultDate ?? startOfDay(resolvedNow),
      onDateChange
    )
    const [editing, setEditing] = React.useState<EditingState | null>(null)

    // Stable, collision-free ids for events created in this instance.
    const idPrefix = React.useId()
    const idCounter = React.useRef(0)
    const genId = () => `${idPrefix}evt-${idCounter.current++}`

    const openAdd = (day: Date, req: AddRequest) => {
      const base = startOfDay(day)
      const startMin = req.startMin ?? 9 * 60
      const start = req.allDay
        ? base
        : new Date(base.getTime() + startMin * 60_000)
      const end = req.allDay
        ? undefined
        : new Date(base.getTime() + (req.endMin ?? startMin + 60) * 60_000)
      setEditing({
        isNew: true,
        event: {
          id: genId(),
          title: '',
          start,
          end,
          allDay: req.allDay,
          color: 'primary',
        },
      })
    }

    const openEdit = (event: CalendarEvent) => {
      setEditing({ isNew: false, event })
    }

    const closeDialog = () => setEditing(null)

    const saveEvent = (next: CalendarEvent) => {
      const exists = events.some((e) => e.id === next.id)
      setEvents(
        exists
          ? events.map((e) => (e.id === next.id ? next : e))
          : [...events, next]
      )
      closeDialog()
    }

    const deleteEvent = (id: string) => {
      setEvents(events.filter((e) => e.id !== id))
      closeDialog()
    }

    const step = (dir: 1 | -1) => {
      if (view === 'month') setDate(addMonths(date, dir))
      else if (view === 'week') setDate(addDays(date, dir * 7))
      else setDate(addDays(date, dir))
    }

    const title =
      view === 'month'
        ? formatMonthYear(date)
        : view === 'week'
          ? formatWeekRange(getWeekDays(date, weekStartsOn))
          : formatDayTitle(date)

    const viewProps: ViewProps = {
      date,
      events,
      weekStartsOn,
      now: resolvedNow,
      density,
      onAdd: openAdd,
      onEditEvent: openEdit,
      onShowDay: (day) => {
        setDate(day)
        setView('day')
      },
    }

    return (
      <div
        ref={ref}
        className={cn(
          'overflow-hidden rounded-xl border border-fbui-gray-200 bg-fbui-base-white',
          className
        )}
      >
        {/* Toolbar */}
        <div
          className={cn(
            'flex flex-wrap items-center justify-between gap-3 border-b border-fbui-gray-200',
            compact ? 'p-2' : 'p-3 sm:p-4'
          )}
        >
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <Button
                variant="primary"
                appearance="outline"
                iconOnly
                size="sm"
                aria-label="Previous"
                onClick={() => step(-1)}
              >
                <CaretLeft />
              </Button>
              <Button
                variant="primary"
                appearance="outline"
                iconOnly
                size="sm"
                aria-label="Next"
                onClick={() => step(1)}
              >
                <CaretRight />
              </Button>
            </div>
            <Button
              variant="primary"
              appearance="outline"
              size="sm"
              onClick={() => setDate(startOfDay(resolvedNow))}
            >
              Today
            </Button>
            <h2
              className={cn(
                'ml-1 font-semibold text-fbui-gray-900',
                compact ? 'text-sm' : 'text-base sm:text-lg'
              )}
            >
              {title}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <SegmentedControl
              aria-label="Calendar view"
              size="sm"
              options={VIEW_OPTIONS}
              value={view}
              onValueChange={(v) => setView(v as CalendarView)}
            />
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Plus />}
              onClick={() => openAdd(date, { allDay: true })}
            >
              <span className="hidden sm:inline">Add event</span>
              <span className="sm:hidden">Add</span>
            </Button>
          </div>
        </div>

        {/* Active view */}
        {view === 'month' && <MonthView {...viewProps} />}
        {view === 'week' && (
          <TimeGridView
            {...viewProps}
            variant="week"
            showCurrentTime={showCurrentTime}
          />
        )}
        {view === 'day' && (
          <TimeGridView
            {...viewProps}
            variant="day"
            showCurrentTime={showCurrentTime}
          />
        )}

        {/* Add/edit modal */}
        <OverlayWrapper
          isOpen={editing !== null}
          onOpenChange={(open) => {
            if (!open) closeDialog()
          }}
        >
          <Backdrop className="max-w-md">
            {editing && (
              <EventDialogForm
                key={editing.event.id}
                editing={editing}
                onSave={saveEvent}
                onDelete={deleteEvent}
                onClose={closeDialog}
              />
            )}
          </Backdrop>
        </OverlayWrapper>
      </div>
    )
  }
)
EventCalendar.displayName = 'EventCalendar'
